import { useEffect, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Textarea } from "./ui/textarea";
import { Calendar } from "./ui/calendar";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Calendar as CalendarIcon,
  CheckCircle2,
  Clock,
  Building2,
  User,
  Mail,
  Phone,
  ChevronDown,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { track } from "@/lib/track";

// Configure your webhook URL and credentials here
const WEBHOOK_URL = import.meta.env.VITE_WEBHOOK_URL || "https://automacao-vertice-webhook.eleevait.com.br/webhook/captacao_leads";
const WEBHOOK_USERNAME = import.meta.env.VITE_WEBHOOK_USERNAME || "";
const WEBHOOK_PASSWORD = import.meta.env.VITE_WEBHOOK_PASSWORD || "";

const demoFormSchema = z.object({
  fullName: z.string().min(3, "Nome deve ter no mínimo 3 caracteres").max(100, "Nome muito longo"),
  email: z.string().email("Email inválido").max(255, "Email muito longo"),
  phone: z.string().min(10, "WhatsApp é obrigatório").max(20, "Telefone inválido"),
  companyName: z.string().min(2, "Nome da empresa obrigatório").max(100, "Nome muito longo"),
  companySize: z.enum(["1-50", "51-200", "201-1000", "1000+"], {
    errorMap: () => ({ message: "Selecione o tamanho da empresa" })
  }),
  role: z.string().max(100, "Cargo muito longo").optional(),
  sector: z.string().optional(),
  application: z.string().optional(),
  otherApplication: z.string().optional(),
  preferredDate: z.date().optional(),
  preferredTime: z.enum(["morning", "afternoon", "flexible"]).optional(),
  interests: z.array(z.string()).optional(),
  message: z.string().max(500, "Mensagem muito longa").optional(),
}).refine((data) => {
  // Se preencheu data, horário é obrigatório
  if (data.preferredDate && !data.preferredTime) return false;
  return true;
}, {
  message: "Selecione um horário preferencial",
  path: ["preferredTime"]
});

type DemoFormData = z.infer<typeof demoFormSchema>;

interface DemoSchedulerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultApplication?: string;
  /** Where the form was opened from, for funnel tracking */
  source?: string;
}

const sectors = [
  "Tecnologia/SaaS",
  "Educação",
  "Saúde",
  "Manufatura",
  "Serviços Financeiros",
  "Varejo/E-commerce",
  "Outro",
];

const DemoScheduler = ({ open, onOpenChange, defaultApplication, source = "unknown" }: DemoSchedulerProps) => {
  const [optionalOpen, setOptionalOpen] = useState(!!defaultApplication);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const form = useForm<DemoFormData>({
    resolver: zodResolver(demoFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      companyName: "",
      companySize: undefined,
      role: "",
      sector: "",
      application: defaultApplication || "",
      otherApplication: "",
      preferredDate: undefined,
      preferredTime: undefined,
      interests: [],
      message: "",
    },
  });

  useEffect(() => {
    if (open) track("demo_form_open", { source });
  }, [open, source]);

  const handleClose = () => {
    if (!submitSuccess) track("demo_form_abandon", { source });
    onOpenChange(false);
    setTimeout(() => {
      setOptionalOpen(!!defaultApplication);
      setSubmitSuccess(false);
      setSubmitError(false);
      form.reset();
    }, 300);
  };

  const onSubmit = async (data: DemoFormData) => {
    setIsSubmitting(true);
    setSubmitError(false);

    // Generate reCAPTCHA token
    if (!executeRecaptcha) {
      console.error("reCAPTCHA not yet available");
      toast({
        title: "Erro de Segurança",
        description: "Sistema de proteção ainda carregando. Aguarde um momento.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    let recaptchaToken: string;
    try {
      recaptchaToken = await executeRecaptcha("demo_scheduler_submit");
    } catch (error) {
      console.error("reCAPTCHA error:", error);
      toast({
        title: "Erro de Segurança",
        description: "Não foi possível validar a segurança do formulário.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    const payload = {
      timestamp: new Date().toISOString(),
      source: "aroan_landing_page",
      type: data.preferredDate ? "fale_com_equipe_demo" : "fale_com_equipe_contato",
      recaptchaToken: recaptchaToken,
      contact: {
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
      },
      company: {
        name: data.companyName,
        size: data.companySize,
        sector: data.sector || null,
        role: data.role || null,
        application: data.application || null,
        otherApplication: data.otherApplication || null,
      },
      ...(data.preferredDate && {
        scheduling: {
          preferredDate: format(data.preferredDate, "yyyy-MM-dd"),
          preferredTime: data.preferredTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      }),
      interests: data.interests || [],
      message: data.message || null,
    };

    try {
      // Create Basic Auth header
      const credentials = btoa(`${WEBHOOK_USERNAME}:${WEBHOOK_PASSWORD}`);

      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Basic ${credentials}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSubmitSuccess(true);
      // GA4 recommended event: mark `generate_lead` as a key event in GA4
      track("generate_lead", {
        source,
        form: "demo",
        scheduled: !!data.preferredDate,
        company_size: data.companySize,
        optional_filled: !!(data.role || data.sector || data.application || data.message),
      });
      const isDemo = !!data.preferredDate;
      toast({
        title: isDemo ? "Demo Agendada!" : "Mensagem Enviada!",
        description: isDemo ? "Você receberá uma confirmação por email em breve." : "Nossa equipe entrará em contato em breve.",
      });
    } catch (error) {
      console.error("Webhook submission error:", error);
      setSubmitError(true);
      track("demo_form_error", { source });
      toast({
        title: "Erro ao agendar demo",
        description: "Por favor, tente novamente ou entre em contato diretamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onInvalid = (errors: FieldErrors<DemoFormData>) => {
    track("demo_form_invalid", { source, fields: Object.keys(errors).join(",") });
  };

  const handleOptionalOpenChange = (isOpen: boolean) => {
    setOptionalOpen(isOpen);
    if (isOpen) track("demo_form_optional_open", { source });
  };

  const renderRequired = () => (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="fullName"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Nome Completo
            </FormLabel>
            <FormControl>
              <Input placeholder="João Silva" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Corporativo
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="joao@empresa.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                WhatsApp
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+55 11 99999-9999" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="companyName"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Nome da Empresa
            </FormLabel>
            <FormControl>
              <Input placeholder="Empresa LTDA" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
          control={form.control}
          name="companySize"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tamanho da Empresa</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="1-50">1-50 funcionários</SelectItem>
                  <SelectItem value="51-200">51-200 funcionários</SelectItem>
                  <SelectItem value="201-1000">201-1000 funcionários</SelectItem>
                  <SelectItem value="1000+">1000+ funcionários</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
    </div>
  );

  const renderOptionalProfile = () => (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="sector"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Setor de Atuação</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {sectors.map((sector) => (
                    <SelectItem key={sector} value={sector}>
                      {sector}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cargo/Função</FormLabel>
              <FormControl>
                <Input placeholder="Gerente de TI, Coordenador de Suporte..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="application"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Aplicação de Interesse</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Service Desk / Atendimento">Service Desk / Atendimento</SelectItem>
                <SelectItem value="Qualidade">Qualidade</SelectItem>
                <SelectItem value="Saúde, Segurança e Meio Ambiente">Saúde, Segurança e Meio Ambiente</SelectItem>
                <SelectItem value="Logística">Logística</SelectItem>
                <SelectItem value="Manutenção">Manutenção</SelectItem>
                <SelectItem value="Prevenção de Perdas">Prevenção de Perdas</SelectItem>
                <SelectItem value="Produção">Produção</SelectItem>
                <SelectItem value="Gestão de Processos">Gestão de Processos</SelectItem>
                <SelectItem value="Auditorias e Certificações">Auditorias e Certificações</SelectItem>
                <SelectItem value="Outra">Outra</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {form.watch("application") === "Outra" && (
        <FormField
          control={form.control}
          name="otherApplication"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Especifique a Aplicação</FormLabel>
              <FormControl>
                <Input placeholder="Descreva sua necessidade" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );

  const renderScheduling = () => (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="preferredDate"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Data Preferencial
            </FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full pl-3 text-left font-normal",
                      !field.value && "text-muted-foreground"
                    )}
                  >
                    {field.value ? (
                      format(field.value, "PPP", { locale: ptBR })
                    ) : (
                      <span>Selecione uma data</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={field.value}
                  onSelect={field.onChange}
                  disabled={(date) =>
                    date < new Date() || date.getDay() === 0 || date.getDay() === 6
                  }
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="preferredTime"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <FormLabel className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Horário Preferencial
            </FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3"
              >
                <div>
                  <RadioGroupItem value="morning" id="morning" className="peer sr-only" />
                  <label
                    htmlFor="morning"
                    className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-card p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
                  >
                    <span className="text-2xl mb-2">🌅</span>
                    <span className="font-semibold">Manhã</span>
                    <span className="text-sm text-muted-foreground">9h - 12h</span>
                  </label>
                </div>
                <div>
                  <RadioGroupItem value="afternoon" id="afternoon" className="peer sr-only" />
                  <label
                    htmlFor="afternoon"
                    className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-card p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
                  >
                    <span className="text-2xl mb-2">☀️</span>
                    <span className="font-semibold">Tarde</span>
                    <span className="text-sm text-muted-foreground">14h - 17h</span>
                  </label>
                </div>
                <div>
                  <RadioGroupItem value="flexible" id="flexible" className="peer sr-only" />
                  <label
                    htmlFor="flexible"
                    className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-card p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
                  >
                    <span className="text-2xl mb-2">🕐</span>
                    <span className="font-semibold">Flexível</span>
                    <span className="text-sm text-muted-foreground">Qualquer</span>
                  </label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Clock className="w-4 h-4" />
        Fuso horário: {Intl.DateTimeFormat().resolvedOptions().timeZone}
      </div>
    </div>
  );

  const renderMessage = () => (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Mensagem/Observações (opcional)</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Conte-nos mais sobre seus desafios atuais ou necessidades específicas..."
                className="resize-none min-h-[100px]"
                {...field}
              />
            </FormControl>
            <div className="flex items-center justify-between">
              <FormMessage />
              <p className="text-xs text-muted-foreground">
                {field.value?.length || 0}/500
              </p>
            </div>
          </FormItem>
        )}
      />

      {form.getValues("preferredDate") && (
        <div className="bg-accent/50 rounded-lg p-4 space-y-2 text-sm">
          <p className="font-semibold">Resumo do Agendamento:</p>
          <p className="text-muted-foreground">
            📅 {format(form.getValues("preferredDate"), "PPP", { locale: ptBR })}
          </p>
          <p className="text-muted-foreground">
            🕐 {form.getValues("preferredTime") === "morning" ? "Manhã (9h-12h)" : form.getValues("preferredTime") === "afternoon" ? "Tarde (14h-17h)" : "Flexível"}
          </p>
          <p className="text-muted-foreground">
            ✉️ {form.getValues("email")}
          </p>
        </div>
      )}
    </div>
  );

  const renderSuccess = () => {
    const isDemo = !!form.getValues("preferredDate");

    return (
      <div className="text-center py-8 space-y-6 animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10 text-primary animate-scale-in" />
        </div>
        <div>
          <h3 className="text-2xl font-bold mb-2">
            {isDemo ? "Demo Agendada com Sucesso!" : "Mensagem Enviada com Sucesso!"}
          </h3>
          <p className="text-muted-foreground">
            {isDemo
              ? `Você receberá uma confirmação no email ${form.getValues("email")} nos próximos minutos.`
              : `Nossa equipe entrará em contato através do email ${form.getValues("email")} em breve.`
            }
          </p>
        </div>
        {isDemo && (
          <div className="bg-accent/50 rounded-lg p-4 space-y-1 text-sm">
            <p className="font-semibold">Detalhes:</p>
            <p>📅 {format(form.getValues("preferredDate"), "PPP", { locale: ptBR })}</p>
            <p>🕐 {form.getValues("preferredTime") === "morning" ? "Manhã" : form.getValues("preferredTime") === "afternoon" ? "Tarde" : "Flexível"}</p>
            <p>👤 {form.getValues("fullName")}</p>
            <p>🏢 {form.getValues("companyName")}</p>
          </div>
        )}
        <p className="text-sm text-muted-foreground">
          {isDemo
            ? "Preparamos uma demo personalizada focando nas áreas que você selecionou."
            : "Agradecemos seu interesse no AROAN!"
          }
        </p>
        <Button onClick={handleClose} size="lg" className="w-full">
          Fechar
        </Button>
      </div>
    );
  };

  const renderError = () => (
    <div className="text-center py-8 space-y-6">
      <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
        <AlertCircle className="w-10 h-10 text-destructive" />
      </div>
      <div>
        <h3 className="text-2xl font-bold mb-2">Erro ao Agendar Demo</h3>
        <p className="text-muted-foreground">
          Não foi possível processar seu agendamento. Por favor, tente novamente ou
          entre em contato diretamente.
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-sm">
          <strong>Email alternativo:</strong>{" "}
          <a href="mailto:contato@aroan.com" className="text-primary hover:underline">
            contato@aroan.com
          </a>
        </p>
      </div>
      <div className="flex gap-3">
        <Button
          onClick={() => {
            setSubmitError(false);
          }}
          variant="outline"
          className="flex-1"
        >
          Tentar Novamente
        </Button>
        <Button onClick={handleClose} variant="ghost" className="flex-1">
          Fechar
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        {submitSuccess ? (
          renderSuccess()
        ) : submitError ? (
          renderError()
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center gap-2">
                <CalendarIcon className="w-6 h-6 text-primary" />
                Agendar demo de 30 min
              </DialogTitle>
              <p className="text-muted-foreground text-sm">
                Preencha 5 campos e nossa equipe confirma o horário com você. Sem compromisso.
              </p>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6">
                {renderRequired()}

                <Collapsible open={optionalOpen} onOpenChange={handleOptionalOpenChange} className="rounded-lg border border-border">
                  <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium">
                    Quer adiantar algo? (opcional)
                    <ChevronDown className={cn("h-4 w-4 transition-transform", optionalOpen && "rotate-180")} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-6 border-t border-border px-4 py-4">
                    {renderOptionalProfile()}
                    {renderScheduling()}
                    {renderMessage()}
                  </CollapsibleContent>
                </Collapsible>
              </form>
            </Form>

            <div className="pt-4 border-t">
              <Button
                type="button"
                onClick={form.handleSubmit(onSubmit, onInvalid)}
                disabled={isSubmitting}
                size="lg"
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {form.watch("preferredDate") ? "Confirmar demo" : "Quero agendar minha demo"}
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DemoScheduler;
